import React, { useRef, useState, useCallback, useLayoutEffect } from 'react';
import { useRichTextTarget } from '../../hooks/useRichTextTarget';
import { hasRichHtml, sanitizeRichHtml, getEditablePlainText, normalizeStoredText } from '../../utils/richText';

export default function RichTextField({
  tag: Tag = 'span',
  className = '',
  value = '',
  onChange,
  multiline = false,
  isEditing = true,
  ...rest
}) {
  const ref = useRef(null);
  const isActiveRef = useRef(false);
  const [isActive, setIsActive] = useState(false);
  const renderAsHtml = hasRichHtml(value);
  const displayValue = normalizeStoredText(value);

  useRichTextTarget(isActive, ref.current);

  useLayoutEffect(() => {
    if (!ref.current || isActiveRef.current) return;
    if (renderAsHtml) {
      const safe = sanitizeRichHtml(value);
      if (ref.current.innerHTML !== safe) ref.current.innerHTML = safe;
    } else if (ref.current.textContent !== displayValue) {
      ref.current.textContent = displayValue;
    }
  }, [value, displayValue, renderAsHtml]);

  const save = useCallback(() => {
    if (!ref.current) return;
    const rawHtml = sanitizeRichHtml(ref.current.innerHTML);
    const plainText = getEditablePlainText(ref.current);
    const valueToSave = hasRichHtml(rawHtml) ? rawHtml : plainText;
    onChange(valueToSave);
    isActiveRef.current = false;
    setIsActive(false);
  }, [onChange]);

  if (!isEditing) {
    if (renderAsHtml) {
      return (
        <Tag
          className={`rich-text-content ${className}`.trim()}
          dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(value) }}
          {...rest}
        />
      );
    }
    return (
      <Tag className={className} {...rest}>
        {displayValue}
      </Tag>
    );
  }

  return (
    <Tag
      ref={ref}
      className={`editable-text rich-text-content ${className} ${isActive ? 'editable-text--active' : ''}`.trim()}
      contentEditable
      suppressContentEditableWarning
      onFocus={() => {
        isActiveRef.current = true;
        setIsActive(true);
      }}
      onBlur={() => {
        window.setTimeout(() => {
          if (isActiveRef.current) save();
        }, 0);
      }}
      onKeyDown={(e) => {
        if (!multiline && e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        }
        if (e.key === 'Escape') {
          if (renderAsHtml) ref.current.innerHTML = sanitizeRichHtml(value);
          else ref.current.textContent = displayValue;
          isActiveRef.current = false;
          setIsActive(false);
          ref.current?.blur();
        }
      }}
      title="Click to edit — use the formatting toolbar for bold, color, links"
      {...rest}
    />
  );
}
